import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

interface ExpandableCardProps {
  title: string;
  badge?: string;
  badgeColor?: string;
  summary: React.ReactNode;
  details?: React.ReactNode;
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  headerActions?: React.ReactNode;
}

export const ExpandableCard: React.FC<ExpandableCardProps> = ({
  title,
  badge,
  badgeColor = "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300",
  summary,
  details,
  children,
  defaultExpanded = false,
  isExpanded: controlledExpanded,
  onToggle,
  headerActions,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded((prev) => !prev);
    }
  };

  return (
    <div className="glass rounded-xl border border-slate-200/80 bg-white/70 p-5 shadow-sm transition-all duration-200 hover:shadow-md dark:border-slate-800/80 dark:bg-slate-900/70">
      <div
        className="flex cursor-pointer items-start justify-between gap-4"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <div className="flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
            {badge && (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badgeColor}`}
              >
                {badge}
              </span>
            )}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">{summary}</div>
        </div>

        <div className="flex items-center gap-2">
          {headerActions && (
            <div onClick={(e) => e.stopPropagation()} className="cursor-default">
              {headerActions}
            </div>
          )}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <ChevronDown className="h-4 w-4" />
          </motion.div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
              {details || children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
